import Konva from "konva";
import React from "react";
import { Group } from "react-konva";
import { SpotShape } from "../../../../types";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { getAbsolutePositionOfShape } from "../../helpers/getAbsolutePositionOfShape";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { ShapeCommonProps } from "../../types";
import { DraggableShape } from "../DraggableShape/DraggableShape";
import { ShapeProvider } from "../ShapeContext/ShapeProvider";
import { useShape } from "../ShapeContext/useShape";
import { SpotItem } from "./SpotShapeItem";
import { SpotItemSVG } from "./SpotShapeItemSVG";
import { getOrientationLayout } from "./helpers";
import { getEffectiveSpotAppearance } from "../../../../utils/spots";

export interface SpotShapeComponentProps extends ShapeCommonProps {
  renderMode?: "konva" | "svg";
}

export const SpotShapeComponent = React.memo(
  React.forwardRef<React.ComponentRef<typeof Group>, SpotShapeComponentProps>(
    (
      {
        onDragStart,
        onDragMove,
        onDragEnd,
        onTransformEnd,
        onContextMenu,
        onMouseEnter,
        onMouseLeave,
        highlightShape,
        highlightShapeData,
        onClick,
        onTap,
        draggable,
        renderMode = "konva"
      },
      ref
    ) => {
      const { shape: spotShape } = useShape();
      const { isViewMode, interactiveZones } = useMapCanvas();
      const shape = spotShape as SpotShape;
      // Not gated on isViewMode: the editing canvas also needs this for
      // resource-type styling (e.g. walk-up) that isn't live occupancy.
      // Everything occupancy-related in the supplied record stays inert in
      // the editor because the caller only ever fills it in there — see
      // NewMapEditor's interactiveZones.
      const interactiveZone = shape.resource_id
        ? interactiveZones?.[shape.resource_id]
        : undefined;

      const handleCircleClick = (
        event: Konva.KonvaEventObject<PointerEvent>,
        resourceId?: string
      ) => {
        if (!isViewMode || shape.isInactive) return;
        event.cancelBubble = true;
        if (isViewMode && resourceId) {
          const interactiveZone = interactiveZones?.[resourceId];
          if (interactiveZone) {
            const stage = getWindowKonvaObject().stages[0];
            if (!stage) return;

            const absPos = getAbsolutePositionOfShape(`${shape.id}`, stage);

            interactiveZone.onAction?.({
              x: absPos?.x || 0,
              y: absPos?.y || 0
            });
          }
        }
      };

      // Memoize the layout calculations
      const layouts = React.useMemo(() => {
        return getOrientationLayout(
          getEffectiveSpotAppearance(shape.spotType, shape.spotAppearance),
          shape.orientation,
          shape.labelPlacement,
          shape.labelOrientation || "auto",
          0,
          0,
          shape.spacing || 0,
          shape.rowSpacing || 0,
          shape.width,
          shape.height
        );
      }, [
        shape.orientation,
        shape.labelPlacement,
        shape.width,
        shape.height,
        shape.spacing,
        shape.rowSpacing,
        shape.spotType,
        shape.spotAppearance,
        shape.labelOrientation
      ]);

      const isVertical =
        shape.orientation === "left" || shape.orientation === "right";

      // If renderMode is SVG, return SVG components
      if (renderMode === "svg") {
        return (
          <SpotItemSVG
            item={shape}
            layout={layouts}
            interactiveZone={interactiveZone}
            isHighlighted={highlightShapeData?.targetId === shape.id}
          />
        );
      }

      // Default Konva rendering
      return (
        <Group
          draggable={draggable}
          id={`shape-${shape.id}`}
          x={shape.x}
          y={shape.y}
          rotation={shape.rotation}
          width={shape.width}
          height={shape.height}
          onClick={onClick}
          onTap={onTap}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
          onTransformEnd={onTransformEnd}
          onContextMenu={onContextMenu}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ref={ref}
        >
          {shape.companions?.map((companion) => (
            <ShapeProvider
              key={companion.id}
              shape={companion}
              isSelected={false}
              onSelect={() => {}}
              onUpdate={() => {}}
              isGroupChild={true}
              disabled={true}
            >
              <DraggableShape key={companion.id} />
            </ShapeProvider>
          ))}
          <SpotItem
            key={shape.id}
            item={shape}
            layout={layouts}
            onSpotClick={(e) => {
              handleCircleClick(e, shape.resource_id);
            }}
            interactiveZone={interactiveZone}
            isHighlighted={highlightShapeData?.targetId === shape.id}
            onShapeHighlight={
              highlightShape
                ? () => {
                    highlightShape({ shape, targetId: shape.id });
                  }
                : undefined
            }
          />
        </Group>
      );
    }
  )
);

SpotShapeComponent.displayName = "SpotShapeComponent";
